import React, { useState, useEffect, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableSortLabel,
    TextField,
    Typography,
    Checkbox,
    Button,
    IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VestiyerConfirmationDialog from './VestiyerConfirmationDialog';

export function CustomerApprovalModal({
    open,
    onClose,
    data = [],
    onApproveSingle,
    onApproveAll
}) {
    // Sıralama için state
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('name');

    // Vestiyer No inputlarının state'i
    const [vestiyerNoInputs, setVestiyerNoInputs] = useState({});

    // Checkboxlar için state
    const [selectAll, setSelectAll] = useState(false);
    const [selectedRows, setSelectedRows] = useState({});

    // Onay modalı için state
    const [dialogOpen, setDialogOpen] = useState(false);
    // Onay modalına gönderilecek vestiyer numaraları
    const [vestiyerNumbers, setVestiyerNumbers] = useState([]);

    const handleOpenDialog = () => {
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
    };

    const handleConfirm = () => {
        // Onay modalındaki "Onayla" butonuna basıldığında yapılacaklar
        console.log("Onaylandı!");
    };

    useEffect(() => {
        if (open) {
            // Vestiyer No inputlarını data üzerinden başlatma
            const initialVestiyer = {};
            data.forEach(item => {
                initialVestiyer[item.id] = item.vestiyerNo || '';
            });
            setVestiyerNoInputs(initialVestiyer);
            // Checkboxları sıfırla
            setSelectedRows({});
            setSelectAll(false);
        }
    }, [open, data]);

    const handleRequestSort = (property) => () => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    // Vestiyer No değiştiğinde, eğer boş ise ilgili satırın seçimini kaldır
    const handleVestiyerNoChange = (id) => (event) => {
        const value = event.target.value;
        setVestiyerNoInputs(prev => ({ ...prev, [id]: value }));
        if (value.trim() === '') {
            // Eğer boş ise seçili satır listesinden de kaldır
            setSelectedRows(prev => ({ ...prev, [id]: false }));
        }
    };

    const handleSelectAll = (event) => {
        const checked = event.target.checked;
        setSelectAll(checked);
        const newSelected = {};
        data.forEach(item => {
            // Yalnızca vestiyer no değeri dolu olan satırlar seçilsin
            newSelected[item.id] = vestiyerNoInputs[item.id]?.trim() ? checked : false;
        });
        setSelectedRows(newSelected);
    };

    const handleSelectRow = (id) => (event) => {
        const checked = event.target.checked;
        setSelectedRows(prev => ({ ...prev, [id]: checked }));
    };

    const sortedData = useMemo(() => {
        const dataCopy = [...data];
        dataCopy.sort((a, b) => {
            let aVal, bVal;
            if (orderBy === 'vestiyerNo') {
                aVal = vestiyerNoInputs[a.id] || a.vestiyerNo || '';
                bVal = vestiyerNoInputs[b.id] || b.vestiyerNo || '';
            } else {
                aVal = a[orderBy] || '';
                bVal = b[orderBy] || '';
            }

            if (orderBy === 'paketAdeti') {
                return order === 'asc' ? aVal - bVal : bVal - aVal;
            } else {
                return order === 'asc'
                    ? aVal.toString().localeCompare(bVal.toString())
                    : bVal.toString().localeCompare(aVal.toString());
            }
        });
        return dataCopy;
    }, [data, order, orderBy, vestiyerNoInputs]);

    // Tekli onay
    const handleApproveSingle = (row) => () => {
        const currentVestiyerNo = vestiyerNoInputs[row.id] || row.vestiyerNo || '';
        setVestiyerNumbers([currentVestiyerNo]);
        onApproveSingle?.(row);
        handleOpenDialog();
    };

    // Toplu onay
    const handleApproveAll = () => {
        const selectedData = data.filter(item => selectedRows[item.id]);
        const currentVestiyerNos = selectedData.map(item => vestiyerNoInputs[item.id] || item.vestiyerNo || '');
        setVestiyerNumbers(currentVestiyerNos);
        onApproveAll?.(selectedData);
        handleOpenDialog();
    };

    const hasSelectedRows = Object.values(selectedRows).some(Boolean);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle sx={{ m: 0, p: 2, position: 'relative' }}>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogTitle
                sx={{
                    m: 0,
                    p: 2,
                    position: 'relative',
                    alignItems: 'center',
                    justifyContent: 'center',
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                <Typography sx={{ color: 'rgba(68, 68, 68, 1)', fontWeight: '700', fontSize: '22px' }}>
                    Onay Bekleyen Müşteri Listesi
                </Typography>
                <Typography sx={{ color: 'rgba(182, 182, 182, 1)', fontWeight: '400', fontSize: '16px' }}>
                    Lütfen müşteri işlemini başlatmak için bekleyen işlemleri onaylayınız.
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Table>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: 'white' }}>
                            {/* Header: Select All Checkbox */}
                            <TableCell sx={{ backgroundColor: 'white' }} padding="checkbox">
                                <Checkbox
                                    color="primary"
                                    checked={selectAll}
                                    onChange={handleSelectAll}
                                />
                            </TableCell>
                            {/* Müşteri Adı */}
                            <TableCell sx={{ backgroundColor: 'white' }} sortDirection={orderBy === 'name' ? order : false}>
                                <TableSortLabel
                                    active={orderBy === 'name'}
                                    direction={orderBy === 'name' ? order : 'asc'}
                                    onClick={handleRequestSort('name')}
                                >
                                    Müşteri Adı
                                </TableSortLabel>
                            </TableCell>
                            {/* Vestiyer No */}
                            <TableCell sx={{ backgroundColor: 'white' }} sortDirection={orderBy === 'vestiyerNo' ? order : false}>
                                <TableSortLabel
                                    active={orderBy === 'vestiyerNo'}
                                    direction={orderBy === 'vestiyerNo' ? order : 'asc'}
                                    onClick={handleRequestSort('vestiyerNo')}
                                >
                                    Vestiyer No
                                </TableSortLabel>
                            </TableCell>
                            {/* Paket Adedi */}
                            <TableCell sx={{ backgroundColor: 'white' }} sortDirection={orderBy === 'paketAdeti' ? order : false}>
                                <TableSortLabel
                                    active={orderBy === 'paketAdeti'}
                                    direction={orderBy === 'paketAdeti' ? order : 'asc'}
                                    onClick={handleRequestSort('paketAdeti')}
                                >
                                    Paket Adedi
                                </TableSortLabel>
                            </TableCell>
                            {/* Paket Notu */}
                            <TableCell sx={{ backgroundColor: 'white' }} sortDirection={orderBy === 'paketNotu' ? order : false}>
                                <TableSortLabel
                                    active={orderBy === 'paketNotu'}
                                    direction={orderBy === 'paketNotu' ? order : 'asc'}
                                    onClick={handleRequestSort('paketNotu')}
                                >
                                    Paket Notu
                                </TableSortLabel>
                            </TableCell>
                            {/* Bulk Approve Button in Header */}
                            <TableCell sx={{ backgroundColor: 'white' }}>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    sx={{
                                        width: '100px',
                                        paddingInline: '10.5px',
                                        paddingTop: '5px',
                                        paddingBottom: '5px',
                                        color: 'rgba(107, 114, 128, 1)',
                                        fontWeight: '500',
                                        fontSize: '14px',
                                        border: '1px solid #D1D5DB',
                                        '&:hover': {
                                            backgroundColor: 'white',
                                            border: '1px solid rgba(107, 114, 128, 1)'
                                        }
                                    }}
                                    onClick={handleApproveAll}
                                    disabled={!hasSelectedRows}
                                >
                                    Toplu Onay
                                </Button>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedData.map((row, index) => (
                            <TableRow
                                key={row.id}
                                sx={{
                                    backgroundColor: ((index + 1) % 2 !== 0)
                                        ? 'rgba(249,250,251,1)'
                                        : 'white'
                                }}
                            >
                                {/* Row Checkbox: Vestiyer no boşsa disable */}
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        color="primary"
                                        checked={!!selectedRows[row.id]}
                                        onChange={handleSelectRow(row.id)}
                                        disabled={!vestiyerNoInputs[row.id]?.trim()}
                                    />
                                </TableCell>
                                {/* Müşteri Adı: İsim üstte, telefon numarası altta */}
                                <TableCell>
                                    <div>
                                        <Typography sx={{ fontWeight: '500', fontSize: '14px', color: 'rgba(16, 24, 40, 1)' }}>
                                            {row.name}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            {row.phone}
                                        </Typography>
                                    </div>
                                </TableCell>
                                {/* Vestiyer No: TextField */}
                                <TableCell>
                                    <TextField
                                        variant="outlined"
                                        size="small"
                                        placeholder="Vestiyer Kodu"
                                        value={vestiyerNoInputs[row.id] || ''}
                                        onChange={handleVestiyerNoChange(row.id)}
                                    />
                                </TableCell>
                                {/* Paket Adedi */}
                                <TableCell>
                                    <div style={{
                                        height: '28px',
                                        width: '28px',
                                        paddingInline: '10px',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        display: 'flex',
                                        borderRadius: '8px',
                                        border: '1px solid rgba(208, 213, 221, 1)',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        color: 'rgba(71, 84, 103, 1)',
                                        backgroundColor: 'white'
                                    }}>
                                        {row.paketAdeti}
                                    </div>
                                </TableCell>
                                {/* Paket Notu */}
                                <TableCell>{row.paketNotu}</TableCell>
                                {/* Tekli Onay Butonu: Vestiyer no boş ise disabled */}
                                <TableCell>
                                    <Button
                                        variant="outlined"
                                        sx={{
                                            backgroundColor: 'rgba(239, 248, 255, 1)',
                                            border: '1px solid rgba(178, 221, 255, 1)',
                                            color: 'rgba(23, 92, 211, 1)',
                                            fontWeight: '500',
                                            fontSize: '14px',
                                            paddingInline: '13px',
                                            paddingTop: '6px',
                                            paddingBottom: '6px',
                                            '&:hover': {
                                                backgroundColor: 'rgba(239, 248, 255, 1)',
                                                border: '1px solid rgba(23, 92, 211, 1)'
                                            }
                                        }}
                                        onClick={handleApproveSingle(row)}
                                        disabled={!vestiyerNoInputs[row.id] || vestiyerNoInputs[row.id].trim() === ''}
                                    >
                                        Onayla
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </DialogContent>

            {/* Onay Modalı (VestiyerConfirmationDialog) */}
            <VestiyerConfirmationDialog
                open={dialogOpen}
                vestiyerNumbers={vestiyerNumbers}
                onClose={handleCloseDialog}
                onConfirm={handleConfirm}
            />
        </Dialog>
    );
}
